import { IonAlert, IonButton, IonContent, IonHeader, IonInput, IonPage, useIonViewWillEnter } from "@ionic/react";
import { RouteComponentProps } from "react-router";
import { TABLE_COLUMNS_USERS } from "../utils/Columns";
import { useContext, useState } from "react";
import { User } from "../utils/Interfaces";
import { APIService } from "../network/APIService";
import { DEFAULT_USER } from "../utils/Constants";
import Footer from "../components/Footer";
import Toolbar from "../components/Toolbar";
import RouteText from "../components/RouteText";
import ResultsText from "../components/ResultsText";
import AppContext from "../providers/AppContext";
import CustomTableWithEditDeleteCol from "../components/CustomTableWithEditDeleteCol";
import DeleteUserModal from "../components/DeleteUserModal";
import TableInstructionText from "../components/TableInstructionText";
import ReloadDataBtn from "../components/ReloadDataBtn";
import "./UserManagement.css";
import "../theme/GlobalStyles.css";

const UserManagement: React.FC<RouteComponentProps> = props => {
  const appContext = useContext(AppContext);

  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchText, setSearchText] = useState("");
  const [userToDelete, setUserToDelete] = useState<User>();
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showGetUsersFailedModal, setShowGetUsersFailedModal] = useState(false);

  useIonViewWillEnter(() => {
    if (appContext.jwt === "") {
      // browser refreshed so move back to landing
      props.history.replace("/");
    } else {
      // browser not refreshed so get the data
      getUsers();
    }
  }, []);

  const getUsers = async () => {
    const users = await APIService.fetchUsers(appContext.jwt);
    if (users) {
      setFilteredUsers(users);
      setAllUsers(users);
    } else {
      setShowGetUsersFailedModal(true);
    }
  };

  function handleSearch(searchText: string) {
    setSearchText(searchText);
    let filteredList = [];
    const text = searchText.toLowerCase().trim();
    if (!text || text === "") {
      // reapply filters without search text
      filteredList = allUsers;
    } else {
      // filter list
      filteredList = filteredUsers.filter((value, index, array) => {
        // properties to search through
        const filterArray = [
          value.id.toString(),
          value.displayName.toLowerCase(),
          value.email.toLowerCase(),
          value.groups.toLowerCase(),
        ];
        return filterArray.some(value => value.includes(text));
      });
    }
    setFilteredUsers(filteredList);
  }

  return (
    <IonPage>
      <IonHeader>
        <Toolbar props={props} />
      </IonHeader>
      <IonContent>
        <div className="pageContainer">
          <div className="pageContentContainer">
            <RouteText
              routes={[{ name: "Dashboard", route: "/" }]}
              currentLocation="User Management"
              routeProps={props}
            />
            <h1 className="pageHeader">Users</h1>
            <div id="searchUsersContainer">
              <p id="searchBarTitle">Search</p>
              <IonInput value={searchText} onIonChange={e => handleSearch(e.detail.value!)} />
            </div>
            <div id="addNewUserContainer">
              <ResultsText count={filteredUsers.length} />
              <div>
                <ReloadDataBtn handleClick={getUsers} />
                <IonButton
                  className="yellowBtn"
                  onClick={() => {
                    appContext.setUserToEdit(DEFAULT_USER);
                    props.history.push("new-user");
                  }}
                >
                  + Add new user
                </IonButton>
              </div>
            </div>
            <CustomTableWithEditDeleteCol
              cols={TABLE_COLUMNS_USERS}
              rowData={filteredUsers}
              handleEditItem={user => {
                appContext.setUserToEdit(user);
                props.history.push("/edit-user");
              }}
              handleDeleteItem={user => {
                setUserToDelete(user);
                setShowDeleteUserModal(true);
              }}
            />
            <TableInstructionText text="Search input looks for matches to id, display name, email, or groups." />
            <DeleteUserModal
              user={userToDelete}
              isOpen={showDeleteUserModal}
              handleModalDismiss={deletedUser => {
                if (deletedUser) {
                  // if the user deleted themself, send them back to the login page
                  if (appContext.currentUser?.email === userToDelete?.email) {
                    props.history.push("/login");
                  } else {
                    getUsers();
                  }
                }
                setShowDeleteUserModal(false);
              }}
            />
          </div>
          <Footer />
        </div>
        <IonAlert
          isOpen={showGetUsersFailedModal}
          onDidDismiss={() => setShowGetUsersFailedModal(false)}
          header={"Alert"}
          message={"Failed to get users. Please try again later."}
          buttons={["OK"]}
        />
      </IonContent>
    </IonPage>
  );
};

export default UserManagement;
